import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, TrendingUp, DollarSign, FileText, Download, ChevronLeft, ChevronRight, Receipt, CreditCard, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API } from "@/App";

const MONTHS_NO = ["Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"];

const ReportsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("daily");

  // Daily state
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  // Monthly state
  const now = new Date();
  const [monthlyYear, setMonthlyYear] = useState(now.getFullYear());
  const [monthlyMonth, setMonthlyMonth] = useState(now.getMonth() + 1);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDaily = useCallback(async () => {
    setDailyLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/daily?date=${dailyDate}`, { headers });
      setDailyReport(res.data);
    } catch (err) {
      console.error("Daily report error:", err);
    }
    setDailyLoading(false);
  }, [dailyDate]);

  const fetchMonthly = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/monthly?year=${monthlyYear}&month=${monthlyMonth}`, { headers });
      setMonthlyReport(res.data);
    } catch (err) {
      console.error("Monthly report error:", err);
    }
    setMonthlyLoading(false);
  }, [monthlyYear, monthlyMonth]);

  useEffect(() => { fetchDaily(); }, [fetchDaily]);
  useEffect(() => { fetchMonthly(); }, [fetchMonthly]);

  const shiftDay = (days) => {
    const d = new Date(dailyDate);
    d.setDate(d.getDate() + days);
    setDailyDate(d.toISOString().split("T")[0]);
  };

  const shiftMonth = (dir) => {
    let m = monthlyMonth + dir;
    let y = monthlyYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonthlyMonth(m);
    setMonthlyYear(y);
  };

  const downloadCSV = (report, filename) => {
    if (!report) return;
    const rows = [["Ordrenummer", "Kunde", "Dato", "Belop", "Tips", "Status"]];
    (report.orders || []).forEach(o => {
      rows.push([o.order_number, o.customer_name, o.created_at?.slice(0, 16).replace("T", " "), o.total_amount, o.tip_amount, o.status]);
    });
    rows.push([]);
    rows.push(["TOTALT SALG", "", "", report.total_sales, "", ""]);
    rows.push(["TOTALT TIPS", "", "", "", report.total_tips, ""]);
    rows.push(["TOTAL INKL. TIPS", "", "", report.total_with_tips, "", ""]);
    if (report.gift_cards && report.gift_cards.length > 0) {
      rows.push([]);
      rows.push(["=== GAVEKORT ===", "", "", "", "", ""]);
      rows.push(["Kode", "Fra", "Til", "Anledning", "Belop", ""]);
      report.gift_cards.forEach(gc => {
        rows.push([gc.card_code, gc.sender, gc.recipient, gc.occasion, gc.amount, ""]);
      });
      rows.push(["GAVEKORT TOTALT", "", "", "", report.gift_cards_total, ""]);
    }
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = "#d4af37" }) => (
    <div className="rounded-xl p-5 shadow-lg" style={{ background: '#2d1810', border: `2px solid ${color}` }}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5" style={{ color }} />
        <span className="text-sm font-medium" style={{ color: '#b8a080' }}>{label}</span>
      </div>
      <p className="text-3xl font-bold" style={{ color }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: '#8b7355' }}>{sub}</p>}
    </div>
  );

  const OrderTable = ({ orders }) => (
    <div className="overflow-x-auto mt-6">
      <table className="w-full text-sm" data-testid="orders-table">
        <thead>
          <tr style={{ borderBottom: '2px solid #d4af37' }}>
            <th className="text-left p-3 font-semibold" style={{ color: '#d4af37' }}>Ordre</th>
            <th className="text-left p-3 font-semibold" style={{ color: '#d4af37' }}>Kunde</th>
            <th className="text-left p-3 font-semibold" style={{ color: '#d4af37' }}>Tid</th>
            <th className="text-right p-3 font-semibold" style={{ color: '#d4af37' }}>Belop</th>
            <th className="text-right p-3 font-semibold" style={{ color: '#d4af37' }}>Tips</th>
            <th className="text-center p-3 font-semibold" style={{ color: '#d4af37' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => (
            <tr key={o.order_number || i} style={{ borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
              <td className="p-3 font-mono text-xs" style={{ color: '#f5e6d3' }}>{o.order_number}</td>
              <td className="p-3" style={{ color: '#f5e6d3' }}>{o.customer_name}</td>
              <td className="p-3 text-xs" style={{ color: '#b8a080' }}>{formatDate(o.created_at)}</td>
              <td className="p-3 text-right font-semibold" style={{ color: '#22c55e' }}>kr {o.total_amount},-</td>
              <td className="p-3 text-right" style={{ color: '#d4af37' }}>{o.tip_amount > 0 ? `kr ${o.tip_amount},-` : "-"}</td>
              <td className="p-3 text-center">
                <span className="px-2 py-1 rounded-full text-xs font-semibold" style={{
                  background: o.status === 'completed' ? 'rgba(34,197,94,0.2)' : 'rgba(212,175,55,0.2)',
                  color: o.status === 'completed' ? '#22c55e' : '#d4af37'
                }}>{o.status === 'completed' ? 'Fullfort' : o.status === 'confirmed' ? 'Bekreftet' : 'Betalt'}</span>
              </td>
            </tr>
          ))}
          {orders.length === 0 && (
            <tr><td colSpan={6} className="p-8 text-center" style={{ color: '#8b7355' }}>Ingen online bestillinger for denne perioden</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const GiftCardTable = ({ giftCards }) => (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm" data-testid="giftcards-table">
        <thead>
          <tr style={{ borderBottom: '2px solid #e8c86a' }}>
            <th className="text-left p-3 font-semibold" style={{ color: '#e8c86a' }}>Kode</th>
            <th className="text-left p-3 font-semibold" style={{ color: '#e8c86a' }}>Fra</th>
            <th className="text-left p-3 font-semibold" style={{ color: '#e8c86a' }}>Til</th>
            <th className="text-left p-3 font-semibold" style={{ color: '#e8c86a' }}>Anledning</th>
            <th className="text-right p-3 font-semibold" style={{ color: '#e8c86a' }}>Belop</th>
          </tr>
        </thead>
        <tbody>
          {giftCards.map((gc, i) => (
            <tr key={gc.card_code || i} style={{ borderBottom: '1px solid rgba(232,200,106,0.15)' }}>
              <td className="p-3 font-mono text-xs" style={{ color: '#f5e6d3' }}>{gc.card_code}</td>
              <td className="p-3" style={{ color: '#f5e6d3' }}>{gc.sender || "—"}</td>
              <td className="p-3" style={{ color: '#f5e6d3' }}>{gc.recipient || "—"}</td>
              <td className="p-3 text-xs capitalize" style={{ color: '#b8a080' }}>{gc.occasion || "—"}</td>
              <td className="p-3 text-right font-semibold" style={{ color: '#e8c86a' }}>kr {gc.amount},-</td>
            </tr>
          ))}
          {giftCards.length === 0 && (
            <tr><td colSpan={5} className="p-6 text-center" style={{ color: '#8b7355' }}>Ingen gavekort solgt i denne perioden</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #1a0f0a 0%, #2d1810 100%)' }}>
      {/* Header */}
      <div className="px-4 py-6" style={{ borderBottom: '2px solid rgba(212,175,55,0.3)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/panel050623")} variant="outline" size="sm" className="rounded-full" style={{ borderColor: '#d4af37', color: '#d4af37', background: 'transparent' }}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Tilbake
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#d4af37' }}>Rapporter</h1>
              <p className="text-xs" style={{ color: '#b8a080' }}>Kun nettbetalinger (Nets Easy)</p>
            </div>
          </div>
          <CreditCard className="h-8 w-8" style={{ color: '#d4af37' }} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 p-1 rounded-xl" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <TabsTrigger value="daily" className="rounded-lg px-6 py-2 font-semibold data-[state=active]:text-[#2d1810]" style={{ color: '#d4af37' }} data-testid="tab-daily">
              <Calendar className="h-4 w-4 mr-2" /> Daglig
            </TabsTrigger>
            <TabsTrigger value="monthly" className="rounded-lg px-6 py-2 font-semibold data-[state=active]:text-[#2d1810]" style={{ color: '#d4af37' }} data-testid="tab-monthly">
              <TrendingUp className="h-4 w-4 mr-2" /> Manedlig
            </TabsTrigger>
          </TabsList>

          {/* DAILY TAB */}
          <TabsContent value="daily">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Button onClick={() => shiftDay(-1)} size="sm" variant="outline" className="rounded-full" style={{ borderColor: '#d4af37', color: '#d4af37', background: 'transparent' }} data-testid="daily-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)} className="px-4 py-2 rounded-lg text-center font-semibold" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid #d4af37', color: '#d4af37' }} data-testid="daily-date-input" />
                <Button onClick={() => shiftDay(1)} size="sm" variant="outline" className="rounded-full" style={{ borderColor: '#d4af37', color: '#d4af37', background: 'transparent' }} data-testid="daily-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {dailyReport && dailyReport.orders.length > 0 && (
                <Button onClick={() => downloadCSV(dailyReport, `rapport-${dailyDate}.csv`)} size="sm" className="rounded-full" style={{ background: '#d4af37', color: '#2d1810' }} data-testid="daily-download">
                  <Download className="h-4 w-4 mr-1" /> Last ned CSV
                </Button>
              )}
            </div>

            {dailyLoading ? (
              <div className="text-center py-12"><div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#d4af37', borderTopColor: 'transparent' }}></div></div>
            ) : dailyReport && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard icon={Receipt} label="Bestillinger" value={dailyReport.total_orders} />
                  <StatCard icon={DollarSign} label="Salg" value={`kr ${dailyReport.total_sales},-`} color="#22c55e" sub="Uten tips" />
                  <StatCard icon={TrendingUp} label="Tips" value={`kr ${dailyReport.total_tips},-`} color="#f59e0b" />
                  <StatCard icon={Gift} label="Gavekort" value={`kr ${dailyReport.gift_cards_total || 0},-`} color="#e8c86a" sub={`${dailyReport.gift_cards_sold || 0} solgt`} />
                </div>
                <div className="rounded-xl p-6 shadow-lg" style={{ background: 'rgba(45,24,16,0.8)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#d4af37' }}>Bestillinger</h3>
                  <OrderTable orders={dailyReport.orders} />
                </div>
                {dailyReport.gift_cards && dailyReport.gift_cards.length > 0 && (
                  <div className="rounded-xl p-6 shadow-lg mt-6" style={{ background: 'rgba(45,24,16,0.8)', border: '1px solid rgba(232,200,106,0.3)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="h-5 w-5" style={{ color: '#e8c86a' }} />
                      <h3 className="text-lg font-semibold" style={{ color: '#e8c86a' }}>Gavekort solgt</h3>
                    </div>
                    <GiftCardTable giftCards={dailyReport.gift_cards} />
                  </div>
                )}
              </motion.div>
            )}
          </TabsContent>

          {/* MONTHLY TAB */}
          <TabsContent value="monthly">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Button onClick={() => shiftMonth(-1)} size="sm" variant="outline" className="rounded-full" style={{ borderColor: '#d4af37', color: '#d4af37', background: 'transparent' }} data-testid="monthly-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-6 py-2 rounded-lg text-center font-bold text-lg" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid #d4af37', color: '#d4af37' }} data-testid="monthly-label">
                  {MONTHS_NO[monthlyMonth - 1]} {monthlyYear}
                </span>
                <Button onClick={() => shiftMonth(1)} size="sm" variant="outline" className="rounded-full" style={{ borderColor: '#d4af37', color: '#d4af37', background: 'transparent' }} data-testid="monthly-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {monthlyReport && monthlyReport.orders.length > 0 && (
                <Button onClick={() => downloadCSV(monthlyReport, `rapport-${monthlyYear}-${String(monthlyMonth).padStart(2, '0')}.csv`)} size="sm" className="rounded-full" style={{ background: '#d4af37', color: '#2d1810' }} data-testid="monthly-download">
                  <Download className="h-4 w-4 mr-1" /> Last ned CSV
                </Button>
              )}
            </div>

            {monthlyLoading ? (
              <div className="text-center py-12"><div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#d4af37', borderTopColor: 'transparent' }}></div></div>
            ) : monthlyReport && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard icon={Receipt} label="Bestillinger" value={monthlyReport.total_orders} />
                  <StatCard icon={DollarSign} label="Salg" value={`kr ${monthlyReport.total_sales},-`} color="#22c55e" sub="Uten tips" />
                  <StatCard icon={TrendingUp} label="Tips" value={`kr ${monthlyReport.total_tips},-`} color="#f59e0b" />
                  <StatCard icon={Gift} label="Gavekort" value={`kr ${monthlyReport.gift_cards_total || 0},-`} color="#e8c86a" sub={`${monthlyReport.gift_cards_sold || 0} solgt`} />
                </div>

                {/* Daily breakdown chart */}
                {monthlyReport.daily_breakdown.length > 0 && (
                  <div className="rounded-xl p-6 shadow-lg mb-6" style={{ background: 'rgba(45,24,16,0.8)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#d4af37' }}>Daglig oversikt</h3>
                    <div className="space-y-2">
                      {monthlyReport.daily_breakdown.map(day => {
                        const maxTotal = Math.max(...monthlyReport.daily_breakdown.map(d => d.total));
                        const widthPct = maxTotal > 0 ? (day.total / maxTotal) * 100 : 0;
                        return (
                          <div key={day.date} className="flex items-center gap-3">
                            <span className="text-xs font-mono w-20 flex-shrink-0" style={{ color: '#b8a080' }}>{day.date.slice(5)}</span>
                            <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: 'rgba(212,175,55,0.1)' }}>
                              <div className="h-full rounded-full transition-all" style={{ width: `${widthPct}%`, background: 'linear-gradient(90deg, #d4af37, #22c55e)' }}></div>
                            </div>
                            <span className="text-xs font-semibold w-24 text-right" style={{ color: '#22c55e' }}>kr {Math.round(day.sales)},-</span>
                            <span className="text-xs w-20 text-right" style={{ color: '#f59e0b' }}>tips {Math.round(day.tips)},-</span>
                            <span className="text-xs w-12 text-right" style={{ color: '#b8a080' }}>{day.orders} stk</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="rounded-xl p-6 shadow-lg" style={{ background: 'rgba(45,24,16,0.8)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#d4af37' }}>Alle bestillinger</h3>
                  <OrderTable orders={monthlyReport.orders} />
                </div>

                {/* Gift cards section */}
                <div className="rounded-xl p-6 shadow-lg mt-6" style={{ background: 'rgba(45,24,16,0.8)', border: '1px solid rgba(232,200,106,0.3)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="h-5 w-5" style={{ color: '#e8c86a' }} />
                    <h3 className="text-lg font-semibold" style={{ color: '#e8c86a' }}>Gavekort solgt</h3>
                    <span className="ml-auto text-sm font-bold" style={{ color: '#e8c86a' }}>Totalt: kr {monthlyReport.gift_cards_total || 0},-</span>
                  </div>
                  <GiftCardTable giftCards={monthlyReport.gift_cards || []} />
                </div>
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ReportsPage;
